/**
 * useProducts Hook - FULLY FIXED: Stable query keys to prevent infinite requests
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import productService from '../services/productService.js';
import { DEFAULT_PRODUCT_SORT } from '../constants/productSort.js';

// Query keys for consistent caching
const QUERY_KEYS = {
  CATEGORIES: 'categories',
  BRANDS: 'brands',
  PRODUCTS: 'products',
  CATEGORY: 'category',
  BRAND: 'brand',
  PRODUCT: 'product'
};

/**
 * Helper to create stable query keys - FIXED
 */
const createStableQueryKey = (baseKey, options = {}) => {
  if (!options || typeof options !== 'object' || Object.keys(options).length === 0) {
    return [baseKey];
  }
  
  // Create a stable, sorted representation of options
  const stableOptions = {};
  Object.keys(options).sort().forEach(key => {
    if (options[key] !== undefined && options[key] !== null) {
      stableOptions[key] = options[key];
    }
  });
  
  return Object.keys(stableOptions).length > 0 ? [baseKey, stableOptions] : [baseKey];
};

/**
 * Main useProducts hook - FIXED with proper memoization
 */
export const useProducts = (type, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  // CRITICAL FIX: Memoize options based on actual values
  const stableOptions = useMemo(() => {
    const stable = {};
    Object.keys(options).forEach(key => {
      if (key !== 'enabled' && options[key] !== undefined && options[key] !== null) {
        stable[key] = options[key];
      }
    });
    return stable;
  }, [
    options.id,
    options.categoryId,
    options.brandId,
    options.search,
    options.sortBy,
    options.sortDirection,
    options.inStock,
    options.onSale,
    options.backInStock,
    options.page,
    options.pageSize,
    options.hasProducts,
    options.isMed,
    options.isRec,
  ]);

  // FIXED: Create stable query key
  const queryKey = useMemo(() => {
    return createStableQueryKey(type === 'categories' ? QUERY_KEYS.CATEGORIES :
                               type === 'brands' ? QUERY_KEYS.BRANDS :
                               type === 'products' ? QUERY_KEYS.PRODUCTS :
                               type === 'product' ? QUERY_KEYS.PRODUCT : type,
                               stableOptions);
  }, [type, stableOptions]);

  // FIXED: Memoize the query function
  const queryFn = useMemo(() => {
    switch (type) {
      case 'categories':
        return () => productService.getCategories();
      case 'brands':
        return () => productService.getBrands();
      case 'products':
        return () => productService.getProducts(stableOptions);
      case 'product':
        return () => productService.getProduct(stableOptions.id);
      default:
        throw new Error(`Invalid useProducts type: ${type}`);
    }
  }, [type, stableOptions]);

  return useQuery({
    queryKey,
    queryFn,
    enabled: enabled && (type === 'categories' || type === 'brands' || type === 'products' || !!stableOptions.id),
    staleTime: type === 'products' ? 5 * 60 * 1000 : 10 * 60 * 1000,
    cacheTime: type === 'products' ? 15 * 60 * 1000 : 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // CRITICAL: Prevent remounting refetches
    ...queryOptions
  });
};

/**
 * Paginated products hook - COMPLETELY FIXED
 */
export const usePaginatedProducts = (options = {}) => {
  // CRITICAL FIX: Properly memoize options
  const stableOptions = useMemo(() => ({
    categoryId: options.categoryId,
    brandId: options.brandId,
    search: options.search,
    sortBy: options.sortBy || 'ProductName',
    sortDirection: options.sortDirection || 'asc',
    inStock: options.inStock,
    page: options.page || 1,
    pageSize: options.pageSize || 50,
    isMed: options.isMed,
    isRec: options.isRec,
  }), [
    options.categoryId,
    options.brandId,
    options.search,
    options.sortBy,
    options.sortDirection,
    options.inStock,
    options.page,
    options.pageSize,
    options.isMed,
    options.isRec,
  ]);

  // FIXED: Stable query key
  const queryKey = useMemo(() => [
    QUERY_KEYS.PRODUCTS, 
    'paginated', 
    stableOptions
  ], [stableOptions]);

  const result = useQuery({
    queryKey,
    queryFn: () => productService.getProducts(stableOptions),
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // CRITICAL: Prevent remounting refetches
    keepPreviousData: true // Keep previous data while loading new data
  });

  return {
    products: result.data?.products || [],
    isLoading: result.isLoading,
    error: result.error,
    hasNextPage: result.data?.pagination ? 
      result.data.pagination.page < result.data.pagination.totalPages : false,
    loadMore: () => {}, // Placeholder
    isFetchingNextPage: false
  };
};

/**
 * Infinite scroll products hook using useInfiniteQuery
 * Accumulates pages as user scrolls, returns flattened product list
 */
export const useInfiniteProducts = (options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  const stableOptions = useMemo(() => ({
    categoryId: queryOptions.categoryId,
    brandId: queryOptions.brandId,
    search: queryOptions.search,
    sortBy: queryOptions.sortBy || DEFAULT_PRODUCT_SORT,
    sortDirection: queryOptions.sortDirection || 'asc',
    inStock: queryOptions.inStock,
    onSale: queryOptions.onSale,
    backInStock: queryOptions.backInStock,
    pageSize: queryOptions.pageSize || 24,
    isMed: queryOptions.isMed,
    isRec: queryOptions.isRec,
  }), [
    queryOptions.categoryId,
    queryOptions.brandId,
    queryOptions.search,
    queryOptions.sortBy,
    queryOptions.sortDirection,
    queryOptions.inStock,
    queryOptions.onSale,
    queryOptions.backInStock,
    queryOptions.pageSize,
    queryOptions.isMed,
    queryOptions.isRec,
  ]);

  const queryKey = useMemo(() => [
    QUERY_KEYS.PRODUCTS,
    'infinite',
    stableOptions
  ], [stableOptions]);

  const result = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) => productService.getProducts({
      ...stableOptions,
      page: pageParam,
    }),
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  const products = useMemo(() => {
    return result.data?.pages?.flatMap(page => page.products) || [];
  }, [result.data]);

  const pagination = result.data?.pages?.[0]?.pagination || {};

  return {
    products,
    pagination,
    isLoading: result.isLoading,
    error: result.error,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    refetch: result.refetch,
  };
};

/**
 * Infinite scroll new arrivals hook
 */
export const useInfiniteNewArrivals = (options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  const stableOptions = useMemo(() => ({
    sortBy: queryOptions.sortBy || 'dateCreated',
    sortDirection: queryOptions.sortDirection || 'desc',
    pageSize: queryOptions.pageSize || 24,
    isMed: queryOptions.isMed,
    isRec: queryOptions.isRec,
  }), [
    queryOptions.sortBy,
    queryOptions.sortDirection,
    queryOptions.pageSize,
    queryOptions.isMed,
    queryOptions.isRec,
  ]);

  const queryKey = useMemo(() => [
    QUERY_KEYS.PRODUCTS,
    'newArrivals',
    'infinite',
    stableOptions
  ], [stableOptions]);

  const result = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) => productService.getNewArrivals({
      ...stableOptions,
      page: pageParam,
    }),
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  const products = useMemo(() => {
    return result.data?.pages?.flatMap(page => page.products) || [];
  }, [result.data]);

  const pagination = result.data?.pages?.[0]?.pagination || {};

  return {
    products,
    pagination,
    isLoading: result.isLoading,
    error: result.error,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    refetch: result.refetch,
  };
};

// FIXED: All convenience hooks with proper memoization
export const useCategories = (options = {}) => {
  const stableOptions = useMemo(() => options, [
    options.hasProducts,
    options.enabled
  ]);
  return useProducts('categories', stableOptions);
};

export const useBrands = (options = {}) => {
  const stableOptions = useMemo(() => options, [
    options.hasProducts,
    options.pageSize,
    options.enabled
  ]);
  return useProducts('brands', stableOptions);
};

export const useFeaturedBrands = (options = {}) => {
  const stableOptions = useMemo(() => options, [options.enabled]);
  return useQuery({
    queryKey: [QUERY_KEYS.BRANDS, 'featured'],
    queryFn: () => productService.getFeaturedBrands(),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: stableOptions.enabled !== false
  });
};

export const useProduct = (productId, options = {}) => {
  const stableOptions = useMemo(() => ({ 
    ...options, 
    id: productId 
  }), [productId, options.enabled]);
  return useProducts('product', stableOptions);
};

export const useProductsByCategory = (categoryId, options = {}) => {
  const stableOptions = useMemo(() => ({
    ...options,
    categoryId,
    enabled: options.enabled !== false && !!categoryId
  }), [categoryId, options.enabled, options.page, options.pageSize, options.isMed, options.isRec, options.inStock, options.sortBy, options.sortDirection]);
  return useProducts('products', stableOptions);
};

export const useProductsByBrand = (brandId, options = {}) => {
  const stableOptions = useMemo(() => ({
    ...options,
    brandId,
    enabled: options.enabled !== false && !!brandId
  }), [brandId, options.enabled, options.page, options.pageSize, options.isMed, options.isRec, options.inStock, options.sortBy, options.sortDirection]);
  return useProducts('products', stableOptions);
};

export const useProductSearch = (query, options = {}) => {
  const stableOptions = useMemo(() => ({
    ...options,
    search: query,
    enabled: options.enabled !== false && query && query.trim().length >= 2
  }), [query, options.enabled, options.page, options.pageSize]);
  return useProducts('products', stableOptions);
};

export const useNewArrivals = (options = {}) => {
  const stableOptions = useMemo(() => ({
    page: options.page || 1,
    pageSize: options.pageSize || 24,
    sortBy: options.sortBy || 'dateCreated',
    sortDirection: options.sortDirection || 'desc',
    inStock: options.inStock,
    isMed: options.isMed,
    isRec: options.isRec,
  }), [options.page, options.pageSize, options.sortBy, options.sortDirection, options.inStock, options.isMed, options.isRec]);

  const queryKey = useMemo(() => [
    QUERY_KEYS.PRODUCTS,
    'newArrivals',
    stableOptions
  ], [stableOptions]);

  const result = useQuery({
    queryKey,
    queryFn: () => productService.getNewArrivals(stableOptions),
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
    enabled: options.enabled !== false
  });

  return {
    data: result.data,
    products: result.data?.products || [],
    pagination: result.data?.pagination || {},
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch
  };
};

export const useLabReports = (options = {}) => {
  const stableOptions = useMemo(() => ({
    search: options.search || undefined,
    brandId: options.brandId || undefined,
  }), [options.search, options.brandId]);

  const queryKey = useMemo(() => [
    QUERY_KEYS.PRODUCTS,
    'labReports',
    stableOptions
  ], [stableOptions]);

  const result = useQuery({
    queryKey,
    queryFn: () => productService.getLabReports(stableOptions),
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
    enabled: options.enabled !== false
  });

  return {
    data: result.data,
    brands: result.data?.brands || [],
    totalProducts: result.data?.totalProducts || 0,
    totalBatches: result.data?.totalBatches || 0,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch
  };
};

export const useLabReportProducts = (brandId, options = {}) => {
  const queryKey = useMemo(() => [
    QUERY_KEYS.PRODUCTS,
    'labReportProducts',
    brandId
  ], [brandId]);

  const result = useQuery({
    queryKey,
    queryFn: () => productService.getLabReportProductsByBrand(brandId),
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options.enabled !== false && !!brandId
  });

  return {
    products: result.data || [],
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch
  };
};

export default useProducts;